import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Router } from '@angular/router';
import { Permission, User } from './model/user';
import { Urls } from './urls';
import { ApiService } from './ApiService';
import { PrefetchService } from '@/offline/prefetch.service';
import { SyncService } from '@/offline/sync.service';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private currentUserSubject: BehaviorSubject<User>;

  public currentUser: Observable<User>;

  public isLogged = false;
  public isSuperAdmin = false;
  public isAdmin = false;

  constructor(
    private http: HttpClient,
    private router: Router,
    private apiService: ApiService,
    private prefetchService: PrefetchService,
    // Injected solely to force early instantiation - its constructor wires up the `online`
    // listener that replays the offline write queue as soon as connectivity returns.
    private syncService: SyncService,
  ) {
    this.currentUserSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem('currentUser')!)
    );
    this.currentUser = this.currentUserSubject.asObservable();
    this.setRoles();
    if (this.isLogged) this.prefetchService.start();
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  private setRoles() {
    const user = this.currentUserSubject.value;
    this.isLogged    = user !== null;
    this.isSuperAdmin = user?.role === 'superadmin';
    this.isAdmin     = ['superadmin', 'admin'].includes(user?.role ?? '');
  }

  // ── Company selection (superadmin only) ────────────────────────────────────

  get selectedCompanyId(): number | null {
    const v = localStorage.getItem('selectedCompanyId');
    return v ? parseInt(v, 10) : null;
  }

  get selectedCompanyName(): string | null {
    return localStorage.getItem('selectedCompanyName');
  }

  selectCompany(company: { id: number; name: string }) {
    localStorage.setItem('selectedCompanyId', String(company.id));
    localStorage.setItem('selectedCompanyName', company.name);
    this.prefetchService.run();
    this.router.navigate(['/']);
  }

  clearCompanySelection() {
    localStorage.removeItem('selectedCompanyId');
    localStorage.removeItem('selectedCompanyName');
  }

  /** The company id to scope company-wide websocket channels by (e.g. the offline document-device heartbeat) - `selectedCompanyId` for a superadmin (may be null until one is picked), the token's own `company_id` for everyone else. */
  get effectiveCompanyId(): number | null {
    return this.isSuperAdmin ? this.selectedCompanyId : (this.currentUserValue?.company_id || null);
  }

  // ── Panel permissions ───────────────────────────────────────────────────────

  /**
   * Returns true if the current user can access the given panel with the given action.
   * superadmin and admin always return true.
   */
  hasPanel(panel: string, action: keyof Permission = 'can_read'): boolean {
    if (!this.isLogged) return false;
    if (this.isAdmin) return true;
    const perms = this.currentUserValue?.permissions;
    if (!perms) return true;
    const p = perms.find(p => p.panel === panel);
    return p ? !!(p as any)[action] : false;
  }

  /**
   * Returns the allowed document types for the current user.
   * null means all types are allowed (admin, superadmin, or no restriction configured).
   */
  getAllowedDocumentTypes(): string[] | null {
    if (this.isAdmin) return null;
    const perms = this.currentUserValue?.permissions;
    if (!perms) return null;
    const docPerm = perms.find(p => p.panel === 'documents');
    return docPerm?.allowed_document_types ?? null;
  }

  /** `login` is either the user's email or their (optional) username - the backend decides which by the presence of "@". */
  login(login: string | null | undefined, password: string | null | undefined) {
    const entity = { login, password };

    const headers = new HttpHeaders()
      .set('Content-Type', 'application/json')
      .set('Accept', 'text/html, application/xhtml+xml, */*');

    return this.http
      .post<any>(Urls.urls.login, entity, { headers })
      .pipe(
        map((response) => {
          this.clearCompanySelection();

          let user = response.user;
          user.token = response.token;
          user.permissions = response.permissions ?? null;

          localStorage.setItem('currentUser', JSON.stringify(user));
          this.currentUserSubject.next(user);
          this.setRoles();
          this.prefetchService.start();

          let returnUrl = '/';
          if (localStorage.getItem('returnUrl')) {
            returnUrl = localStorage.getItem('returnUrl')!;
            localStorage.removeItem('returnUrl');
          }

          return { returnUrl };
        })
      );
  }

  /** Patches the locally cached name/username/email after a successful self-edit (`ProfileFormComponent`), so the sidebar and anywhere else reading `currentUserValue` reflect the change immediately without a re-login. */
  updateCurrentUserProfile(patch: Partial<Pick<User, 'name' | 'username' | 'email'>>): void {
    const user = { ...this.currentUserValue, ...patch };
    localStorage.setItem('currentUser', JSON.stringify(user));
    this.currentUserSubject.next(user);
  }

  logout() {
    this.apiService.save(Urls.urls.logout, {}).subscribe({
      next: () => this.removeSession(),
      error: () => this.removeSession(),
    });
  }

  private removeSession() {
    localStorage.removeItem('currentUser');
    this.clearCompanySelection();
    this.currentUserSubject.next(null!);
    this.setRoles();
    this.prefetchService.stop();
    this.router.navigate(['/login']);
  }
}
